import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin, Alert, Modal, Form, Input, Button } from 'antd';
import { LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children, requiredPermission, requiredRole }) => {
  const { isAuthenticated, loading, user, hasPermission, hasRole, cambiarPassword } = useAuth();
  const location = useLocation();

  // Loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        gap: 16
      }}>
        <Spin size="large" />
        <p style={{ color: '#6b7280', margin: 0 }}>
          Verificando autenticación...
        </p>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role permission
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Alert
          message="Acceso Denegado"
          description="No tienes permisos suficientes para acceder a esta página"
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Check specific permission
  if (requiredPermission && !hasPermission(requiredPermission.resource, requiredPermission.action)) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Alert
          message="Permisos Insuficientes"
          description={`Necesitas permisos de ${requiredPermission.action} para ${requiredPermission.resource}`}
          type="warning"
          showIcon
        />
      </div>
    );
  }

  // Password change required
  if (user?.requiere_cambio_password) {
    return <CambioPasswordRequerido />;
  }

  // All checks passed, render children
  return children;
};

// Componente para cambio forzado de contraseña
const CambioPasswordRequerido = () => {
  const { cambiarPassword, user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await cambiarPassword(values.passwordActual, values.passwordNueva);
      // El contexto se encargará de actualizar el estado
    } catch (error) {
      // El error ya se maneja en el contexto
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '16px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '450px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <LockOutlined style={{ fontSize: '48px', color: '#fbbf24', marginBottom: '16px' }} />
          <h2 style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
            Cambio de Contraseña Requerido
          </h2>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Hola <strong>{user?.nombre_persona}</strong>, debes cambiar tu contraseña para continuar
          </p>
        </div>

        <Alert
          message="Seguridad"
          description="Por motivos de seguridad, debes establecer una nueva contraseña antes de acceder al sistema."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            label="Contraseña Actual"
            name="passwordActual"
            rules={[
              { required: true, message: 'Ingresa tu contraseña actual' }
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Contraseña actual"
              iconRender={(visible) => 
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          <Form.Item
            label="Nueva Contraseña"
            name="passwordNueva"
            rules={[
              { required: true, message: 'Ingresa una nueva contraseña' },
              { min: 8, message: 'La contraseña debe tener al menos 8 caracteres' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                message: 'Debe contener al menos: 1 mayúscula, 1 minúscula, 1 número y 1 símbolo'
              }
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Nueva contraseña segura"
              iconRender={(visible) => 
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          <Form.Item
            label="Confirmar Nueva Contraseña"
            name="confirmarPassword"
            dependencies={['passwordNueva']}
            rules={[
              { required: true, message: 'Confirma tu nueva contraseña' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('passwordNueva') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Las contraseñas no coinciden'));
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Confirmar nueva contraseña"
              iconRender={(visible) => 
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                border: 'none',
                height: '48px',
                borderRadius: '8px',
                fontWeight: '600'
              }}
            >
              {loading ? 'Actualizando...' : 'Cambiar Contraseña'}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
            Una vez actualizada, podrás acceder normalmente al sistema
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProtectedRoute;